import React, { useContext, useCallback, useMemo, useEffect, useState } from 'react';
import Header from 'components/header/Header';
import {
  DisplayModes,
  GetDisplayMode,
  navigationBlockWarningMessage,
  SetDisplayMode,
} from 'utils/Global';
import { ExperimentHeaderProps, ROIClientDTO } from 'business/models/CustomModels';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  selectIsEditable,
  selectedDrawingState,
  selectROIs,
} from 'redux/selectors/StateSelectors';
import { setIsWellImagingEnabled } from 'redux/reducers/experimentReducer';
import { ExpDashboardContext } from './ExperimentDashboardContext';
import { usageStatLogger } from 'components/usagestat/UsageStat';
import './ExperimentHeaderStyles.scss';
import ExperimentCommandButtons from './ExperimentCommandButtons';
import {
  getAddLayerList,
  GetAddLayerListParams,
  getDesignHeaderButtons,
  GetDesignHeaderButtonsParams,
  getImageHeaderButtons,
  GetImageHeaderButtonsParams,
  getInfoHeaderButtons,
  GetInfoHeaderButtonsParams,
  getPrintActionsList,
  getReportsButtonsList,
  GetReportsButtonsListParams,
  getScoreHeaderButtons,
  GetScoreHeaderButtonsParams,
  getScreenDesignHeaderButtons,
  GetScreenDesignHeaderButtonsParams,
  getWellImageHeaderButtons,
  GetWellImageHeaderButtonsParams,
  screenStockReportLabel,
} from 'components/Experiment/utils';
import { EDrawingType } from 'components/plateviewer/well/Well';
import {
  BooleanPropertyDTO,
  PlateDropRegions,
  WellSelectionDTO,
} from 'business/models/Models';
import { FileExportView } from 'components/plateviewer/experiment/FileExportView';
import { EFileExportTypes, EFileImportTypes } from 'enums/EFileExportTypes';
import { useLicenseAuthorizeCommand } from 'CustomHooks/useLicenseAuthorizeCommand';
import { EUserAction } from 'constants/index';
import useRMLite from 'CustomHooks/useRMLite';
import WellService from 'business/services/WellService';
import { useModalHandlers } from 'components/modal/hooks';
import { ManageImages } from './Setup/Imaging/ManageImages';
import { setWellDropsImagingEnabled } from 'redux/reducers/experimentDataReducer';
import { usePlateDisableImageRegions } from 'components/commandpanel/Navigator/hooks';
import {
  useAllWellNavigationDetails,
  useLastSelectedWellNavigationDetails,
  useScoreExperimentWellsInProgressCount,
  useSelectedDropImageParameters,
  useWellDropImportFlToggle,
} from 'redux/hooks/experiment';
import { useIsScreenInUse } from 'redux/hooks/common';
import { useExperimentRightClickOptions } from 'components/rightClick/hooks/experimentHooks';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import {
  useInspectionChange,
  useIsWellImagingEnabled,
  useMarkInteresting,
  useMarkInterestingWellDrop,
  useToggleWellDropImage,
} from 'components/Experiment/hooks';
import { EReportTypes } from 'enums/EIconPanels';
import DropImageSettings from 'components/commandpanel/ExperimentPanel/ExperimentTools/DropImageSettings';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';
import {
  ELayerDesignSelectedTabIndex,
  setLayerDesignSelectedTabIndex,
} from 'redux/reducers/layerReducer';
import { ESidePanelTabs } from './constants';
import {
  useSaveDiscardButtons,
  UseSaveDiscardButtonsParams,
} from 'CustomHooks/useSaveDiscardButtons';

export default function ExperimentHeader({
  expName,
  changeCompareMode,
  headerType,
  saveExperiment,
  onDiscardButtonClick,
  changeIconPanels,
  isDirty,
  accessRightName,
  setShowOptimize,
  setReportType,
  pathName,
  setShowSeedFormulation,
  saveImageClick,
  editExperimentClick,
  sidePanelHandlers,
  setImageView,
  setScoreView,
  exportPoi,
  deleteInspection,
  printBarcode,
  isPasteNotAllowed,
  isHeaderButtonsDisabled,
  rotateCanvas,
  markAsDispensed,
  exportFrapDataPoints,
  disableFrapExport,
  isDropImageAvailable,
  disablePrintBarcode,
  breadCrumbs,
  inspections,
  isImagedExperiment,
  isResourceAvailable,
}: ExperimentHeaderProps) {
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const allWellNavigationDetails = useAllWellNavigationDetails();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName, inspection } = selectedDropImageParameters || {};
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const isWellImageEnable = useIsWellImagingEnabled(dropNumber);
  const selectedDrawing = useRMSelector(selectedDrawingState);
  const getPlateDisableImageRegions = usePlateDisableImageRegions();
  const wellDropRegions = getPlateDisableImageRegions(plateID);
  const rois: ROIClientDTO[] = useRMSelector((state) =>
    selectROIs(state, dropNumber),
  ) as ROIClientDTO[];
  const dispatch = useRMDispatch();
  const displayMode = GetDisplayMode();
  const isCompareMode = pathName ? pathName.includes('/compare') : false;
  const isScreenMode = pathName ? pathName.includes('/screen') : false;
  const isWellMode = pathName ? pathName.includes('/well') : false;
  const expDashboardContext = useContext(ExpDashboardContext);
  const expData = useRMSelector((state) => state.experiment.expData);
  const licenseAuthCommand = useLicenseAuthorizeCommand();
  const rmLite = useRMLite();
  const { showModal, hideModal } = useModalHandlers();
  const isScreenInUse: boolean = useIsScreenInUse();
  const setExperimentRightClick = useExperimentRightClickOptions();
  const isEditingAllowed = useSystemProperty<BooleanPropertyDTO>(
    PropertyConstants.AllowEditingOfReadonlyExperiment,
    false,
  );

  const [experimentClipboardState, setExperimentClipboardState] = useState({
    experimentCopy: null,
    experimentPaste: null,
    isCopyDisabled: false,
    isPasteDisabled: true,
  });

  const onInspectionChange = useInspectionChange(inspections);
  const [importFl, setImportFl] = useWellDropImportFlToggle();
  const toggleWellDropImage = useToggleWellDropImage();
  const markInterestingWellDrop = useMarkInterestingWellDrop();
  const markInteresting = useMarkInteresting();
  const scoreExperimentWellsInProgressCount = useScoreExperimentWellsInProgressCount();

  const openLayersExplorerPanel = useCallback(() => {
    dispatch(setLayerDesignSelectedTabIndex(ELayerDesignSelectedTabIndex.Explorer));
    expDashboardContext.setSelectedSidePanelTab(ESidePanelTabs.Layers);
  }, [expDashboardContext.setSelectedSidePanelTab]);

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const editExperimentClickHandler = useCallback(
    () => editExperimentClick(),
    [editExperimentClick],
  );
  const ShowOptimizeModalHandler = licenseAuthCommand(
    () => setShowOptimize(true),
    EUserAction.Optimization,
  );

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const createSeedHandler = useCallback(() => setShowSeedFormulation(true), []);

  const isSelectedROIImaged = useCallback(() => {
    const region = rois.find((r) => r.id === selectedDrawing.id);
    if (region) {
      return region.imagingEnabled;
    }
    return true;
  }, [rois, selectedDrawing]);

  const getDisableImageLabel = () => {
    let label = '';
    if (displayMode === DisplayModes.IMAGE) {
      const getImageEnable = (x: PlateDropRegions) => {
        return (
          x.wellNumber === wellNum &&
          x.dropNumber === dropNumber &&
          x.regionName === regionName
        );
      };
      const imgEnabled = wellDropRegions?.filter(getImageEnable)[0]?.imagingEnabled;
      label = imgEnabled ? 'Disable Imaging' : 'Enable Imaging';
      dispatch(setIsWellImagingEnabled(imgEnabled));
    } else if (displayMode === DisplayModes.WELLIMAGE) {
      if (selectedDrawing?.type === EDrawingType.ROI) {
        label = isSelectedROIImaged() ? 'Disable Imaging' : 'Enable Imaging';
      } else {
        label = isWellImageEnable ? 'Disable Imaging' : 'Enable Imaging';
      }
    }
    return label;
  };

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const toggleWellDropImaging = useCallback(async () => {
    dispatch(
      setWellDropsImagingEnabled({
        wellDropRegions: [
          { plateId: plateID, wellNumber: wellNum, dropNumber: dropNumber },
        ],
        imagingEnabled: !isWellImageEnable,
      }),
    );
    await new WellService().setWellDropImagingEnabled(
      [
        {
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber: dropNumber,
        },
      ] as WellSelectionDTO[],
      !isWellImageEnable,
    );
  }, [setWellDropsImagingEnabled, plateID, wellNum, dropNumber, isWellImageEnable]);

  const manageImaging = async () => {
    showModal({
      body: <ManageImages hideModal={hideModal} expTreeNodeId={expData.treeNodeId} />,
      heading: 'Manage Images',
      dialogClassName: 'min-w-[492px] m-0',
      isCancelOnly: false,
      showButtons: false,
      hideFooter: true,
    });
  };

  const reportsListParams: GetReportsButtonsListParams = useMemo(
    () => ({
      accessRightName,
      GetDisplayMode,
      setReportType,
      usageStatLogger,
      licenseAuthCommand,
      SetDisplayMode,
      exportFrapDataPoints,
      disableFrapExport,
    }),
    [
      accessRightName,
      GetDisplayMode,
      setReportType,
      usageStatLogger,
      licenseAuthCommand,
      SetDisplayMode,
      exportFrapDataPoints,
      disableFrapExport,
    ],
  );

  const addLayerListParams: GetAddLayerListParams = useMemo(
    () => ({
      accessRightName,
      changeIconPanels,
      isEditMode,
      isScreenMode,
      authCommand: licenseAuthCommand,
      isScreenInUse,
      openLayersExplorerPanel,
    }),
    [
      accessRightName,
      changeIconPanels,
      isEditMode,
      isScreenMode,
      licenseAuthCommand,
      isScreenInUse,
      openLayersExplorerPanel,
    ],
  );

  const saveDiscardButtonsParams: UseSaveDiscardButtonsParams = {
    enableButtons: isDirty && !isHeaderButtonsDisabled,
    onSave: saveExperiment,
    onDiscard: onDiscardButtonClick,
    navigationBlockCallback: (location) => {
      const urlParts = location?.pathname.split('/');
      if (
        (!location ||
          (urlParts.length > 1 && urlParts[1] !== 'exp' && urlParts[1] !== 'screen')) &&
        isResourceAvailable
      ) {
        if (isDirty) {
          return navigationBlockWarningMessage;
        } else if (scoreExperimentWellsInProgressCount) {
          return 'There are pending scoring operations. Are you sure you want to leave?';
        }
      }
    },
  };

  /** Adding call back/useMemo to this list is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const reportsList = useMemo(
    () => getReportsButtonsList(reportsListParams),
    [reportsListParams],
  );

  /** Adding call back/useMemo to this list is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const addLayerList = useMemo(
    () => getAddLayerList(addLayerListParams),
    [addLayerListParams],
  );

  const SaveDiscardButtons = useSaveDiscardButtons(saveDiscardButtonsParams);

  const printActionsList = useMemo(
    () => getPrintActionsList(printBarcode),
    [printBarcode, plateID],
  );

  const designHeaderButtonsParams: GetDesignHeaderButtonsParams = {
    addLayerList,
    reportsList,
    printActionsList,
    editExperiment: editExperimentClickHandler,
    showOptimizationModal: ShowOptimizeModalHandler,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    setFileImportActionType: expDashboardContext.setFileImportActionType,
    printBarcode,
    accessRightName,
    isEditMode,
    isDisabled: isHeaderButtonsDisabled,
    isIngredientView: false,
    markAsDispensed,
    disablePrintBarcode,
    isEditingAllowed: isEditingAllowed,
    isRMLite: rmLite,
  };

  const imageHeaderButtonsParams: GetImageHeaderButtonsParams = {
    saveImage: saveImageClick,
    changeIconPanels,
    ShowOptimizeModalHandler,
    reportsList,
    accessRightName,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    exportPoi,
    deleteInspection,
    createSeedHandler,
    isDisabled: isHeaderButtonsDisabled,
    getDisableImageLabel,
    setRegionImaging: toggleWellDropImaging,
    rmLite,
    manageImaging,
  };

  const scoreHeaderButtonsParams: GetScoreHeaderButtonsParams = {
    ShowOptimizeModalHandler,
    reportsList,
    accessRightName,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    exportPoi,
    isDisabled: isHeaderButtonsDisabled,
    rmLite,
  };

  const wellImageButtonsParams: GetWellImageHeaderButtonsParams = {
    isCompareMode,
    isWellMode,
    compareModeHandler: changeCompareMode,
    accessRightName,
    createSeedHandler,
    ShowOptimizeModalHandler,
    saveImage: saveImageClick,
    reportsList,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    isDisabled: isHeaderButtonsDisabled,
    getDisableImageLabel,
    setRegionImaging: toggleWellDropImaging,
    rmLite,
    exportPoi,
    isDropImageAvailable,
  };

  const infoHeaderButtonsParams: GetInfoHeaderButtonsParams = {
    reportsList,
    printActionsList,
    printBarcode,
    isDisabled: isHeaderButtonsDisabled,
    disablePrintBarcode,
    rmLite,
  };

  const screenDesignHeaderButtonsParams: GetScreenDesignHeaderButtonsParams = {
    accessRightName,
    addLayerList,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    setFileImportActionType: expDashboardContext.setFileImportActionType,
    reportsList,
    printActionsList,
    showOptimizationModal: ShowOptimizeModalHandler,
    printBarcode,
    isDisabled: isHeaderButtonsDisabled,
    rotateCanvas,
    disablePrintBarcode,
  };

  const designHeaderButtons = getDesignHeaderButtons({
    ...designHeaderButtonsParams,
    isIngredientView: displayMode === DisplayModes.WELLINGREDIENT ? true : false,
    isDispensed: expData?.isDispensed,
  });
  const imageHeaderButtons = getImageHeaderButtons(imageHeaderButtonsParams);
  const scoreHeaderButtons = getScoreHeaderButtons(scoreHeaderButtonsParams);
  const wellImageHeaderButtons = getWellImageHeaderButtons(wellImageButtonsParams);
  const infoHeaderButtons = getInfoHeaderButtons(infoHeaderButtonsParams);
  const screenDesignHeaderButtons = getScreenDesignHeaderButtons(
    screenDesignHeaderButtonsParams,
  );

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const inspectionMenuItems = useMemo(() => {
    return inspections?.map((item) => ({
      label: item.name,
      onClick: () => onInspectionChange(item.id),
      isSelected: item.id === inspection?.id,
      shouldShowSelectedItemCheckmark: true,
    }));
  }, [inspections, inspection]);

  const dropImageSettings = (
    <DropImageSettings customizeSettingsClassName="" disabled={false} />
  );

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const exportOptions = useMemo(
    () => [
      {
        label: 'Export XML Screen',
        onClick: () =>
          expDashboardContext.setFileExportActionType(EFileExportTypes.XMLSCREEN),
        isDisabled: !(accessRightName === ETreeNodeAccessRightNames.Modify),
      },
      {
        label: 'Export Scores',
        onClick: licenseAuthCommand((e) => {
          setReportType(EReportTypes.ScoreReport);
          usageStatLogger('Report.' + e.target.textContent);
        }, EUserAction.ScoreReport),
        isDisabled: !(accessRightName === ETreeNodeAccessRightNames.Modify),
      },
      {
        label: 'Export Selected Wells',
        onClick: () => expDashboardContext.setFileExportActionType(EFileExportTypes.Text),
        isDisabled: !(accessRightName === ETreeNodeAccessRightNames.Modify),
      },
    ],
    [expDashboardContext.setFileExportActionType, accessRightName, licenseAuthCommand],
  );

  /** Only add methods which are memoized, otherwise it can cause infinite re-rendering.
  Deal with caution */

  const actionMap = useMemo(
    () => ({
      'Add Layer Tool': {
        action: () => {},
        subMenu: addLayerList,
      },
      'Edit Experiment': {
        action: editExperimentClickHandler,
        isDisabled: isEditMode,
      },
      'Import XML Screen': {
        action: () => {
          expDashboardContext.setFileImportActionType(EFileImportTypes.XMLSCREEN);
        },
        isDisabled:
          !isEditMode || !(accessRightName === ETreeNodeAccessRightNames.Modify),
      },
      Export: {
        action: () => {},
        subMenu: exportOptions,
      },
      Reports: {
        action: () => {},
        subMenu: reportsList.filter((item) => item.label != screenStockReportLabel),
      },
      'New Seed': {
        action: createSeedHandler,
        isDisabled: isHeaderButtonsDisabled,
      },
      'Mark as Dispensed': {
        action: markAsDispensed,
        isDisabled:
          expData?.isDispensed ||
          !markAsDispensed ||
          accessRightName !== ETreeNodeAccessRightNames.Modify ||
          isHeaderButtonsDisabled,
      },
      Copy: {
        action: experimentClipboardState.experimentCopy,
        isDisabled: experimentClipboardState.isCopyDisabled,
      },
      Paste: {
        action: experimentClipboardState.experimentPaste,
        isDisabled: experimentClipboardState.isPasteDisabled,
      },
      Inspections: {
        action: () => {},
        subMenu: inspectionMenuItems,
      },
      'Save Image': {
        action: saveImageClick,
      },
      'Disable Image': {
        action: toggleWellDropImage,
        isSelected: isImagedExperiment ? !isWellImageEnable : false,
        isDisabled: !isImagedExperiment,
      },
      'Enable Import Focus Level': {
        action: setImportFl,
        isSelected: isImagedExperiment ? importFl : false,
        isDisabled: !isImagedExperiment,
      },
      'Drop Captures': {
        action: () => {},
        customSubmenuComponent: dropImageSettings,
      },
      'Mark Interesting': {
        action: () => markInterestingWellDrop('i', markInteresting),
        isSelected: markInteresting,
      },
      'Instant Drop Report': {
        action: licenseAuthCommand((e) => {
          setReportType(EReportTypes.InstantDropReport);
          usageStatLogger('Report.' + e.target.textContent);
        }, EUserAction.InstantDropReport),
        isDisabled: !(accessRightName === ETreeNodeAccessRightNames.Modify),
      },
      'Single Drop Report': {
        isDisabled: !(accessRightName === ETreeNodeAccessRightNames.Modify),
        action: licenseAuthCommand((e) => {
          setReportType(EReportTypes.SingleDropReport);
          usageStatLogger('Report.' + e.target.textContent);
        }, EUserAction.SingleDropReport),
      },
    }),
    [
      addLayerList,
      editExperimentClickHandler,
      isEditMode,
      expDashboardContext.setFileImportActionType,
      expDashboardContext.setFileExportActionType,
      reportsList,
      createSeedHandler,
      markAsDispensed,
      experimentClipboardState,
      inspectionMenuItems,
      saveImageClick,
      toggleWellDropImage,
      setImportFl,
      markInterestingWellDrop,
      setReportType,
      licenseAuthCommand,
      markInteresting,
    ],
  );

  useEffect(() => {
    if (!isScreenMode) {
      setExperimentRightClick(actionMap, displayMode);
    }
  }, [actionMap, expName]);

  function getHeader(name) {
    let view;
    switch (name) {
      case DisplayModes.DESIGN:
        // to do: We need to handel the Screen and Design mode properly, currently its mixed up
        view = isScreenMode ? (
          <Header
            headerButtons={screenDesignHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedActionSection={SaveDiscardButtons}
            extendedCommandSection={
              <ExperimentCommandButtons
                isDisabled={isHeaderButtonsDisabled}
                isPasteNotAllowed={isPasteNotAllowed}
                setExperimentClipboardState={setExperimentClipboardState}
              />
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        ) : (
          <Header
            headerButtons={designHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedActionSection={SaveDiscardButtons}
            extendedCommandSection={
              <ExperimentCommandButtons
                isDisabled={isHeaderButtonsDisabled}
                isPasteNotAllowed={isPasteNotAllowed}
                setExperimentClipboardState={setExperimentClipboardState}
              />
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.WELLINGREDIENT:
        view = (
          <Header
            headerButtons={designHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedActionSection={!rmLite ? SaveDiscardButtons : []}
            extendedCommandSection={
              !rmLite ? (
                <ExperimentCommandButtons
                  isDisabled={isHeaderButtonsDisabled}
                  isPasteNotAllowed={isPasteNotAllowed}
                  setExperimentClipboardState={setExperimentClipboardState}
                />
              ) : null
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.IMAGE:
        view = (
          <Header
            headerButtons={imageHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedCommandSection={
              !rmLite ? (
                <ExperimentCommandButtons
                  isDisabled={isHeaderButtonsDisabled}
                  isPasteNotAllowed={isPasteNotAllowed}
                  setExperimentClipboardState={setExperimentClipboardState}
                />
              ) : null
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.SCORE:
        view = (
          <Header
            headerButtons={scoreHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedCommandSection={
              !rmLite ? (
                <ExperimentCommandButtons
                  isDisabled={isHeaderButtonsDisabled}
                  isPasteNotAllowed={isPasteNotAllowed}
                  setExperimentClipboardState={setExperimentClipboardState}
                />
              ) : null
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.WELLIMAGE:
        view = (
          <Header
            headerButtons={wellImageHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedCommandSection={
              !isWellMode &&
              !rmLite && (
                <ExperimentCommandButtons
                  isDisabled={isHeaderButtonsDisabled}
                  isPasteNotAllowed={isPasteNotAllowed}
                  setExperimentClipboardState={setExperimentClipboardState}
                />
              )
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.EXPINFO:
        view = (
          <Header
            headerButtons={infoHeaderButtons}
            title={expName}
            isNewTheme={true}
            extendedActionSection={SaveDiscardButtons}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.SCREEN:
        view = (
          <Header
            headerButtons={screenDesignHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedActionSection={SaveDiscardButtons}
            extendedCommandSection={
              <ExperimentCommandButtons
                isDisabled={isHeaderButtonsDisabled}
                isPasteNotAllowed={isPasteNotAllowed}
                setExperimentClipboardState={setExperimentClipboardState}
              />
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.REPORT:
        view = <Header headerButtons={[]} title={expName} />;
        break;
      default:
        break;
    }
    return view;
  }

  return (
    <>
      {getHeader(headerType)}

      {expDashboardContext.fileExportActionType != EFileExportTypes.NONE && (
        <FileExportView
          exportType={expDashboardContext.fileExportActionType}
          setExportType={expDashboardContext.setFileExportActionType}
          selectedWells={allWellNavigationDetails}
          expData={expData}
        />
      )}
    </>
  );
}
